/**
 * What kind of device this is, and the choices that depend on it.
 *
 * The position lock (asked for 2026-10-10): on a phone or tablet a picture
 * moves only after "Edit position", because a stray touch or a scroll that
 * starts on the card used to move pictures that had been lined up
 * (2026-09-27). With a mouse that cannot happen by accident, so on a computer
 * the selected picture is dragged, zoomed and turned straight away. Either way
 * the person can change it - in the editor or on the account page - and the
 * choice is kept for this device (a phone and a computer usually want
 * different answers, so it is not kept on the account).
 */
import { useEffect, useState } from 'react';
import { t } from './strings.ts';

/** A phone or tablet: the main pointer is a finger. A touchscreen laptop with a mouse or trackpad is not. */
export const isTouchDevice = () => {
  try {
    return matchMedia('(pointer: coarse)').matches;
  } catch {
    return false;
  }
};

// a new key (2026-10-17): every device starts again from the default below
const LOCK_KEY = 'fmc.positionLock2';

/**
 * The lock this device starts with: off on any computer with a mouse or trackpad -
 * a touchscreen laptop too (owner, 2026-10-17: "with PC login make the default
 * unlocked") - and on only where a finger is the only pointer.
 */
export const positionLockDefault = () => {
  try {
    return !matchMedia('(any-pointer: fine)').matches;
  } catch {
    return false;
  }
};

function readPositionLock(): boolean {
  try {
    const s = localStorage.getItem(LOCK_KEY);
    return s === null ? positionLockDefault() : s === '1';
  } catch {
    return positionLockDefault();
  }
}

/** Whether pictures are locked until "Edit position", and a way to change it (kept on this device). */
export function usePositionLock(): [boolean, (v: boolean) => void] {
  const [locked, setLocked] = useState(readPositionLock);
  useEffect(() => {
    // another tab changed it
    const f = (e: StorageEvent) => { if (e.key === LOCK_KEY) setLocked(readPositionLock()); };
    addEventListener('storage', f);
    return () => removeEventListener('storage', f);
  }, []);
  const set = (v: boolean) => {
    setLocked(v);
    try { localStorage.setItem(LOCK_KEY, v ? '1' : '0'); } catch { /* the choice lasts until the page closes */ }
  };
  return [locked, set];
}

/** The account page's setting. */
export function PositionLockSetting() {
  const [locked, setLocked] = usePositionLock();
  return (
    <section className="settings">
      <h2>{t('movingPictures')}</h2>
      <label className="switch"><input type="checkbox" checked={locked} onChange={(e) => setLocked(e.target.checked)} />
        {' '}<span aria-hidden="true">🔒</span> {t('lockPositions')}</label>
      <p className="muted small">{t('lockPositionsHint')}</p>
      <p className="muted small">{positionLockDefault() ? t('lockDefaultTouch') : t('lockDefaultMouse')}</p>
    </section>
  );
}
